import { HEALTH_HEALTHY, STATUS_RUNNING } from "@stores/instances/constants";
import {
  HOSTING_FEATURE_CUSTOM_DOMAIN,
  HOSTING_FEATURE_IP_ALLOWLIST,
  HOSTING_FEATURE_TRIAL_UP,
  SUBSCRIPTION_STATUS_ACTIVE,
  SUBSCRIPTION_STATUS_CANCELLATION_PENDING,
  SUBSCRIPTION_STATUS_CANCELLED,
  SUBSCRIPTION_STATUS_TRIAL,
} from "@stores/subscriptions/constants";
import {
  CreateInstanceDataType,
  IInstanceSetupData,
  IPurchase,
  IPurchaseTrialUp,
} from "./types";
import {
  isDevPlan,
  isEnterprisePlan,
  isProPlan,
  isStarterPlan,
} from "@common/utils/plan";
import { captureException } from "@sentry/nextjs";
import { ADDON_PRODUCT_DWH_TYPE } from "@stores/addons/constants";
import { buildPlanAlias } from "@stores/plans/utils";
import { PlanAliasType } from "@stores/plans/types";
import { PLAN_ALIAS_INTERNAL } from "@stores/plans/constants";

export function getCreateInstanceData({
  planAlias,
  isSelfHosted = false,
  isYearly = false,
  isDev = false,
  dnsAlias: dns_alias,
  region,
  migrationId: migration_id,
  upsertAddOns: add_ons,
}: IInstanceSetupData): CreateInstanceDataType | null {
  if (planAlias) {
    const plan_alias = buildPlanAlias({
      basePlanAlias: planAlias,
      isSelfHosted,
      isYearly,
      isDev,
    });

    return {
      plan_alias,

      // Cloud only
      dns_alias,
      region,
      add_ons,

      // For migration
      migration_id,
    };
  } else {
    captureException(new Error("getCreateInstanceData: planObj is undefined"));
  }

  return null;
}

export function getPrettyPlanName({
  subscription,
  product,
}: Pick<IPurchase, "subscription" | "product">): string {
  const isDWHStr = hasDWH({ subscription }) ? " + Storage" : "";
  return `${product.plan.billing_details_name || product.plan.short_name}${isDWHStr}`;
}

export function getPrettyPlanTier(planAlias: PlanAliasType) {
  if (isProPlan(planAlias)) {
    return "Pro";
  }
  if (isStarterPlan(planAlias)) {
    return "Starter";
  }
  if (planAlias === PLAN_ALIAS_INTERNAL) {
    return "Internal";
  }
  return null;
}

export function isCanceledOrDeleted({
  subscription,
  hosted_instance,
}: Pick<IPurchase, "subscription" | "hosted_instance">): boolean {
  return (
    subscription.status === SUBSCRIPTION_STATUS_CANCELLED ||
    hosted_instance?.is_deleted === true ||
    (subscription.is_canceled &&
      subscription.status !== SUBSCRIPTION_STATUS_CANCELLATION_PENDING)
  );
}

export function isBlocked({
  subscription,
  hosted_instance,
}: Pick<IPurchase, "subscription" | "hosted_instance">): boolean {
  return (
    isCanceledOrDeleted({
      subscription,
      hosted_instance,
    }) || hosted_instance?.health !== HEALTH_HEALTHY
  );
}

export function isDeletingDevInstances(
  purchases: IPurchase[],
  purchase: IPurchase,
): boolean {
  const proPurchases = purchases.filter(
    ({ product, subscription }) =>
      !isDevPlan(product.plan.alias) &&
      isProPlan(product.plan.alias) &&
      subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
  );

  const isLastActiveProPlan =
    !proPurchases.length ||
    (proPurchases.length === 1 && proPurchases[0].id === purchase.id);

  const hasDevPurchases = !!purchases.find(
    ({ product, subscription }) =>
      isDevPlan(product.plan.alias) &&
      subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
  );

  return isLastActiveProPlan && hasDevPurchases;
}

// Sort by:
//  - status: trial/active, followed by cancellation-pending, followed by cancelled
//  - within each status, sort by purchase date with recent purchase at the top
export function comparePurchase(
  purchase1: Pick<IPurchase, "subscription" | "hosted_instance">,
  purchase2: Pick<IPurchase, "subscription" | "hosted_instance">,
): number {
  return (
    statusScore(purchase2) - statusScore(purchase1) ||
    purchase2.subscription.created_at.localeCompare(
      purchase1.subscription.created_at,
    )
  );
}

function statusScore({
  subscription,
  hosted_instance,
}: Pick<IPurchase, "subscription" | "hosted_instance">): number {
  if (
    isCanceledOrDeleted({
      subscription,
      hosted_instance,
    })
  ) {
    return 0;
  } else if (subscription.status === SUBSCRIPTION_STATUS_CANCELLATION_PENDING) {
    return 1;
  } else {
    return 2;
  }
}

// changing plan
// - purchase is not canceled
// - instance is `running` OR self-hosted
// - subscription is `active` OR in `trial`
export function canChangePlan({
  hosted_instance,
  subscription,
}: Pick<IPurchase, "subscription" | "hosted_instance">): boolean {
  return (
    !isCanceledOrDeleted({ hosted_instance, subscription }) &&
    !!(
      (!hosted_instance || hosted_instance.status === STATUS_RUNNING) &&
      (subscription.status === SUBSCRIPTION_STATUS_ACTIVE ||
        subscription.status === SUBSCRIPTION_STATUS_TRIAL)
    )
  );
}

// DWH
export function hasDWH({
  subscription,
}: Pick<IPurchase, "subscription">): boolean {
  const { subscribed_products } = subscription;
  return !!subscribed_products.find(
    ({ product_type }) => product_type === ADDON_PRODUCT_DWH_TYPE,
  );
}

function hasHostingFeature(
  { hosting_features }: Pick<IPurchase, "hosting_features">,
  hostingFeature: string,
): boolean {
  if (hosting_features) {
    return !!(Object.keys(hosting_features).indexOf(hostingFeature) > -1);
  }

  return false;
}

// We allow custom domain only if present in the hosting features list
export function canHaveACustomDomain(
  purchase: Pick<IPurchase, "hosting_features">,
): boolean {
  return hasHostingFeature(purchase, HOSTING_FEATURE_CUSTOM_DOMAIN);
}

// We allow IP allowlisting only if present in the hosting features list
export function canHaveIPAllowlist(
  purchase: Pick<IPurchase, "hosting_features">,
): boolean {
  return hasHostingFeature(purchase, HOSTING_FEATURE_IP_ALLOWLIST);
}

// Can create new production instance?
export function canAccountCreateNewProductionInstance(
  purchases: Pick<IPurchase, "subscription">[],
): boolean {
  const purchasesInTrial = purchases.filter(
    (purchase) => purchase.subscription.in_trial,
  );
  return !purchasesInTrial || (purchasesInTrial && purchasesInTrial.length < 2);
}

// Can create new development instance?
export function canAccountCreateNewDevelopmentInstance(
  purchases: Pick<IPurchase, "product" | "subscription">[],
): boolean {
  const activeProPurchases = purchases
    .filter(({ product }) => isProPlan(product.plan.alias))
    .filter(
      (purchase) => purchase.subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
    );
  return !!activeProPurchases.length;
}

// Enterprise

export function hasAtLeastOneActiveEnterprisePlan(
  purchases: IPurchase[],
): boolean {
  return !!purchases
    .filter(({ product }) => isEnterprisePlan(product.plan.alias))
    .find(
      ({ subscription }) =>
        subscription.status === SUBSCRIPTION_STATUS_TRIAL ||
        subscription.status === SUBSCRIPTION_STATUS_ACTIVE,
    );
}

// Is purchase eligible for pro trial?
export function canTryProPlan(
  purchase: Pick<IPurchase, "hosting_features" | "subscription">,
): boolean {
  return (
    hasHostingFeature(purchase, HOSTING_FEATURE_TRIAL_UP) &&
    purchase.subscription.status === SUBSCRIPTION_STATUS_ACTIVE
  );
}

// Pro trial
export function getProTrial({
  trial_ups,
}: Pick<IPurchase, "trial_ups">): IPurchaseTrialUp | undefined {
  return trial_ups?.find(({ status }) => !!status);
}

// Quota
